import { memo, useCallback, useMemo } from 'react';
import { Tooltip } from 'react-tooltip';
import styled, { css } from 'styled-components';

import { getThemeBadgeColor } from '../../utility/badgeColors';
import { convertTimeToMMSS } from '../../utility/timeFormat';
import {
  ClipContextTooltip,
  DARK_TOOLTIP_STYLE,
} from '../common/ClipContextTooltip';
import DurationOverlayBadge from '../common/DurationOverlayBadge';
import MuxPlayerClip from '../common/MuxPlayerClip';
import { SelectionOrderBadge } from '../common/SelectionOrderBadge';
import { TranscriptExcerptPanel } from '../common/TranscriptExcerptPanel';
import ImpactScoreBadge from './ImpactScoreBadge';

import type { GenerationContext } from '../../services/AIClipProducer';
import type { BadgeColor } from '../../utility/badgeColors';
import type { TooltipEntry } from '../common/ClipContextTooltip';
import type { AspectRatio } from '@src/types.ts/video';

function buildContextEntries(
  fragment: { speakerName?: string },
  generationContext?: GenerationContext,
): TooltipEntry[] {
  const entries: TooltipEntry[] = [];

  if (generationContext?.customPrompt) {
    entries.push({ label: 'Custom', value: generationContext.customPrompt });
  }
  if (generationContext?.themes?.length) {
    entries.push({
      label: 'Prompt',
      value: generationContext.themes.join(', '),
    });
  }
  if (generationContext?.suggestedTheme) {
    entries.push({ label: 'Topic', value: generationContext.suggestedTheme });
  }
  if (generationContext?.speakers?.length) {
    entries.push({
      label: 'Speakers',
      value: generationContext.speakers.join(', '),
    });
  }
  if (generationContext?.goal) {
    entries.push({ label: 'Goal', value: generationContext.goal });
  }
  if (generationContext?.audiences?.length) {
    entries.push({
      label: 'Audience',
      value: generationContext.audiences.join(', '),
    });
  }
  if (fragment.speakerName && !generationContext?.speakers?.length) {
    entries.push({ label: 'Speaker', value: fragment.speakerName });
  }

  return entries.filter(e => e.value);
}

const AIClipMemoized = memo(
  ({
    fragment,
    index,
    isSelected,
    selectionOrder,
    onSelect: onSelectByIndex,
    isPlaying,
    onPlay: onPlayByIndex,
    aspectRatio,
    generationContext,
    poster,
    suppressDerivedPoster,
    posterOnly,
  }: {
    fragment: any;
    isSelected: boolean;
    selectionOrder: number | null;
    onSelect: (index: number) => void;
    isPlaying: boolean;
    onPlay: (index: number) => void;
    index: number;
    aspectRatio?: AspectRatio;
    generationContext?: GenerationContext;
    poster?: string;
    suppressDerivedPoster?: boolean;
    posterOnly?: boolean;
  }) => {
    const onSelect = useCallback(
      () => onSelectByIndex(index),
      [onSelectByIndex, index],
    );

    const onPlay = useCallback(() => {
      onPlayByIndex(index);
    }, [onPlayByIndex, index]);

    return (
      <AIClip
        fragment={fragment}
        isSelected={isSelected}
        selectionOrder={selectionOrder}
        onSelect={onSelect}
        isPlaying={isPlaying}
        onPlay={onPlay}
        index={index}
        aspectRatio={aspectRatio}
        generationContext={generationContext}
        poster={poster}
        suppressDerivedPoster={suppressDerivedPoster}
        posterOnly={posterOnly}
      />
    );
  },
);

const AIClip = memo(
  ({
    fragment,
    isSelected,
    selectionOrder,
    onSelect,
    isPlaying,
    onPlay,
    index,
    aspectRatio,
    generationContext,
    poster,
    suppressDerivedPoster,
    posterOnly,
  }: {
    fragment: any;
    isSelected: boolean;
    /**
     * 1-based position in the selection — the position this clip takes in a
     * combined reel (PLAT-11093); `null` when the clip is not selected.
     */
    selectionOrder: number | null;
    onSelect: () => void;
    isPlaying: boolean;
    onPlay: () => void;
    index: number;
    aspectRatio?: AspectRatio;
    generationContext?: GenerationContext;
    poster?: string;
    suppressDerivedPoster?: boolean;
    posterOnly?: boolean;
  }) => {
    const duration = Math.round(fragment.duration);
    const durationFormatted = duration ? convertTimeToMMSS(duration) : '';

    const title = fragment.title;
    const description = fragment.text;
    const startTimeFormatted = convertTimeToMMSS(
      Math.round(fragment.startTime),
    );
    const endTimeFormatted = convertTimeToMMSS(
      Math.round(fragment.startTime + fragment.duration),
    );

    const isCustomPrompt =
      !!generationContext?.customPrompt && !generationContext?.themes?.length;
    const isReel = !!fragment.isReel;

    const badgeLabel = useMemo(() => {
      if (isReel) return 'Combined Clips';
      if (isCustomPrompt) return 'Custom Prompt';
      return fragment.themeDisplayName || fragment.theme;
    }, [isReel, isCustomPrompt, fragment.themeDisplayName, fragment.theme]);

    const badgeColor = useMemo((): BadgeColor | 'orange' | 'purple' => {
      if (isReel) return 'purple';
      if (isCustomPrompt) return 'orange';
      return getThemeBadgeColor(fragment.theme);
    }, [isReel, isCustomPrompt, fragment.theme]);

    const contextEntries = useMemo(
      () => buildContextEntries(fragment, generationContext),
      [fragment, generationContext],
    );

    const tooltipId = `theme-tooltip-${index}`;

    return (
      <Card onClick={onSelect} $isSelected={isSelected}>
        {/* The position this clip takes in a combined reel, not a tick: the
            selection order *is* the reel's running order (PLAT-11093 DE11).
            Gold fill lives with the badge (PLAT-11094). */}
        <SelectMarker
          data-testid={`ai-clip-${index}`}
          position={selectionOrder}
        />
        <ScoreRow>
          <ImpactScoreBadge
            impactScore={fragment.impactScore}
            id={`impact-score-${index}`}
          />
        </ScoreRow>
        {/* Full-bleed video, edge to edge like the saved clip card's thumbnail;
            the source time range rides the canvas just above its controls. */}
        <VideoBleed data-card-canvas="">
          <MuxPlayerClip
            clip={{
              ...fragment,
              poster: poster || fragment.poster,
              suppressDerivedPoster,
              posterOnly,
            }}
            isPlaying={isPlaying}
            onPlay={onPlay}
            aspectRatio={aspectRatio}
          />
          {/* Duration on the canvas, the saved clip card's corner badge. */}
          {durationFormatted && (
            <CanvasDuration>{durationFormatted}</CanvasDuration>
          )}
        </VideoBleed>
        {title && <Title>{title}</Title>}
        {/* Display speaker badge if clip has speaker information */}
        {fragment.speakerName && (
          <SpeakerBadge>{fragment.speakerName}</SpeakerBadge>
        )}
        {/* Transcript opener under the title, the saved clip card's recipe:
            two clamped lines; hovering reveals the full excerpt in a
            popover. */}
        <TranscriptSnippet
          {...(description
            ? { 'data-tooltip-id': `ai-clip-transcript-${index}` }
            : { 'aria-hidden': true })}
        >
          {description}
        </TranscriptSnippet>
        {description && (
          <Tooltip
            id={`ai-clip-transcript-${index}`}
            place="bottom-start"
            noArrow
            style={DARK_TOOLTIP_STYLE}
            opacity={1}
            render={() => (
              <TranscriptExcerptPanel text={description} title="Transcript" />
            )}
          />
        )}
        <MetaRow>
          <TimeRanges>
            {startTimeFormatted} - {endTimeFormatted}{' '}
          </TimeRanges>
          <ThemeTag
            $color={badgeColor}
            data-tooltip-id={tooltipId}
            data-tooltip-content="-"
          >
            {badgeLabel}
          </ThemeTag>
          {(contextEntries.length > 0 || isCustomPrompt) && (
            <Tooltip
              id={tooltipId}
              style={DARK_TOOLTIP_STYLE}
              opacity={1}
              render={() => (
                <ClipContextTooltip
                  entries={contextEntries}
                  title={isReel ? 'Combined Clips' : undefined}
                />
              )}
            />
          )}
        </MetaRow>
      </Card>
    );
  },
);

export default AIClipMemoized;

/** The shared badge, positioned in the card's corner; styling lives with it.
    The figure carries the grid marker's semi-bold so the pick's position reads
    the same on both surfaces (PLAT-11138). Declared before Card so the card's
    hover rule can reference it. */
const SelectMarker = styled(SelectionOrderBadge)`
  position: absolute;
  right: 16px;
  top: 16px;
  cursor: pointer;
  font-weight: var(--font-weight-semi-bold);
  transition:
    background-color var(--transition-fast) ease,
    border-color var(--transition-fast) ease;
`;

/* The header band, holding the impact score alongside the select marker.

   It reserves its height even when a clip has no score, which is a legitimate
   state everywhere — `impactScore` is optional end to end and only arrives when
   the Impact Score prompt resolves, so `ImpactScoreBadge` renders nothing. An
   empty band collapsed the row to 0, which put the full-bleed canvas at the
   marker's own 16px offset; both are positioned with `z-index: auto`, so the
   later canvas painted straight over the marker and a scoreless clip had no
   visible pick at all. The saved clip card reserves its badge row the same way
   (`VideoBadgeRow`). 34px is the 28px figure's rendered box in Inter (33px),
   plus a pixel so a fallback face can't make a scored card the taller one. */
const ScoreRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  min-height: 34px;
  margin-bottom: 8px;
`;

/* Pinned to the card's bottom edge, so the time range and theme tag line up
   across a row once the cards stretch to equal height. */
const MetaRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: auto;
`;

const Card = styled.div<{ $isSelected: boolean }>`
  position: relative;
  display: flex;
  flex-direction: column;
  /* Slimmer on top: the header row wants the saved clip card's 8px band, not
     a full content gutter — its own margin supplies the 8px below. */
  padding: 8px 16px 16px;
  border-radius: 8px;
  background-color: var(--surface);
  box-sizing: border-box;
  color: var(--text-secondary);
  /* The one-line title is nowrap, so the card's automatic minimum size is the
     whole untruncated title — in the results' 3 x 1fr grid that floors the
     track at the longest title, leaving columns unequal and the row
     overflowing. Floor it at 0 so the tracks stay equal thirds and the title
     ellipsizes instead. */
  min-width: 0;
  /* No height, so the grid stretches every card in a row to the tallest — an
     optional speaker badge otherwise left the row's bottoms ragged. The saved
     clip card sets no height either. */
  cursor: pointer;
  transition:
    box-shadow var(--transition-normal) ease,
    border-color var(--transition-normal) ease;
  /* The Video Clips card shell (PLAT-11138): borderless surface at rest, and a
     pick shows the grid's green selected border — so a clip looks the same
     mid-selection here as it does once saved to the grid. */
  border: 1.5px solid
    ${props => (props.$isSelected ? 'var(--primary)' : 'transparent')};

  /* Hover lifts the card a full step, no border recolor — the grid's hover. */
  &:hover {
    box-shadow: var(--elevation-md);
  }

  /* The whole card is the hit target, so hovering it answers "what does a
     click do?" by arming the marker — in the button standard's own language
     (gray escalation, never accent): the ring brightens from muted to
     foreground, which is what stays legible at 18px on this dark surface;
     the elevated fill underneath is a secondary hint, too faint to carry it
     alone. Scoped to unpicked cards so a selected marker keeps its gold, and
     suspended while the pointer is on the canvas — a click there goes to the
     player, not to selection, so the marker rests. */
  ${props =>
    !props.$isSelected &&
    css`
      &:hover:not(:has([data-card-canvas]:hover)) ${SelectMarker} {
        border-color: var(--foreground);
        background-color: var(--surface-elevated);
      }
    `}
`;

const TimeRanges = styled.div`
  color: var(--text-muted);
  font-family: var(--font-primary);
  font-size: 10px;
  font-weight: var(--font-weight-medium);
  line-height: normal;
`;

/* Spans the card's 16px gutters so the canvas runs edge to edge, like the
   saved clip card's thumbnail. The player's own rounding and border are inline
   styles shared with other surfaces, so the full-bleed override outranks them
   here instead of changing them everywhere. */
const VideoBleed = styled.div`
  position: relative;
  margin: 0 -16px;

  mux-player {
    border: none !important;
    border-radius: 0 !important;
  }
`;

/* The shared corner badge, moved to the canvas's top-right — the bottom edge
   belongs to the player's controls here. It never takes the pointer, so a
   click in that corner still reaches the player. */
const CanvasDuration = styled(DurationOverlayBadge)`
  top: 8px;
  bottom: auto;
  pointer-events: none;
  z-index: 2;
`;

/* Always exactly one line (ellipsized), the saved clip card's title rule, so
   the transcript opener sits at the same height on every card. */
const Title = styled.div`
  margin-top: 12px;
  color: var(--foreground);
  font-size: var(--text-sm);
  font-weight: var(--font-weight-medium);
  line-height: 1.4;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

/* Two-line transcript opener under the title (the saved clip card's "row
   summary sub-line"); the slot keeps its height when a clip has no transcript
   so cards stay aligned. */
const TranscriptSnippet = styled.div`
  margin: 4px 0 16px;
  color: var(--text-secondary);
  font-size: var(--text-xs);
  font-weight: var(--font-weight-normal);
  line-height: var(--leading-text-xs);
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  min-height: calc(var(--leading-text-xs) * 2);
`;

const ThemeTag = styled.div<{ $color?: string }>`
  display: inline-flex;
  align-items: center;
  padding: 4px 12px;
  border-radius: 999px;
  background-color: ${props => `var(--badge-${props.$color || 'blue'}-bg)`};
  color: ${props => `var(--badge-${props.$color || 'blue'}-text)`};
  font-size: var(--text-xs);
  font-weight: var(--font-weight-medium);
  line-height: var(--leading-text-xs);
  white-space: nowrap;
  width: fit-content;
  text-transform: capitalize;
`;

const SpeakerBadge = styled.div`
  display: inline-flex;
  padding: 4px 0px;
  margin-bottom: 6px;
  font-size: 10px;
  font-weight: var(--font-weight-medium);
  line-height: normal;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;
